/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import AutosizeInput from 'react-input-autosize';
import { EuiScreenReaderOnly } from '../../accessibility';
import { EuiFormControlLayout } from '../../form/form_control_layout';
import { EuiComboBoxPill } from './combo_box_pill';
import { htmlIdGenerator } from '../../../services';
export class EuiComboBoxInput extends Component {
  state = {
    hasFocus: false
  };
  updatePosition = () => {
    // Wait a beat for the DOM to update, since we depend on DOM elements' bounds.
    requestAnimationFrame(() => {
      this.props.updatePosition();
    });
  };
  onFocus = event => {
    this.props.onFocus(event);
    this.setState({
      hasFocus: true
    });
  };
  onBlur = event => {
    if (this.props.onBlur) {
      this.props.onBlur(event);
    }

    this.setState({
      hasFocus: false
    });
  };

  componentDidUpdate(prevProps) {
    const {
      searchValue
    } = prevProps; // We need to update the position of everything if the user enters enough input to change
    // the size of the input.

    if (searchValue !== this.props.searchValue) {
      this.updatePosition();
    }
  }

  inputOnChange = event => {
    const {
      onChange,
      searchValue
    } = this.props;

    if (onChange) {
      onChange(event.target.value);
    }
  };
  inputRefCallback = ref => {
    const {
      autoSizeInputRef
    } = this.props;

    if (autoSizeInputRef) {
      autoSizeInputRef(ref);
    }
  };

  render() {
    const {
      compressed,
      focusedOptionId,
      fullWidth,
      hasSelectedOptions,
      id,
      inputRef,
      isDisabled,
      isListOpen,
      noIcon,
      onClear,
      onClick,
      onCloseListClick,
      onOpenListClick,
      onRemoveOption,
      placeholder,
      rootId,
      searchValue,
      selectedOptions,
      singleSelection: singleSelectionProp,
      toggleButtonRef,
      value,
      prepend,
      append
    } = this.props;
    const singleSelection = Boolean(singleSelectionProp);
    const asPlainText = singleSelectionProp && typeof singleSelectionProp === 'object' && singleSelectionProp.asPlainText || false;
    const pills = selectedOptions ? selectedOptions.map(option => {
      const {
        label,
        color,
        onClick,
        ...rest
      } = option;
      const pillOnClose = isDisabled || singleSelection || onClick ? undefined : onRemoveOption;
      return <EuiComboBoxPill option={option} onClose={pillOnClose} key={label.toLowerCase()} color={color} onClick={onClick} onClickAriaLabel={onClick ? 'Change' : undefined} asPlainText={asPlainText} {...rest}>
              {label}
            </EuiComboBoxPill>;
    }) : null;
    let removeOptionMessage;
    let removeOptionMessageId;

    if (this.state.hasFocus) {
      const readPlaceholder = placeholder ? `${placeholder}.` : '';
      const removeOptionMessageContent = `Combo box. Selected. ${searchValue ? `${searchValue}. Selected. ` : ''}${selectedOptions && selectedOptions.length > 0 ? `${value}. Press Backspace to delete ${selectedOptions[selectedOptions.length - 1].label}. ` : ''}Combo box input. ${readPlaceholder} Type some text or, to display a list of choices, press Down Arrow. ` + 'To exit the list of choices, press Escape.';
      removeOptionMessageId = htmlIdGenerator()(); // aria-live="assertive" will read this message aloud immediately once it enters the DOM.
      // We'll render to the DOM when the input gains focus and remove it when the input loses focus.
      // We'll use aria-hidden to prevent default aria information from being read by the screen
      // reader.

      removeOptionMessage = <EuiScreenReaderOnly>
          <span aria-live="assertive" id={removeOptionMessageId}>
            {removeOptionMessageContent}
          </span>
        </EuiScreenReaderOnly>;
    }

    let placeholderMessage;

    if (placeholder && selectedOptions && !selectedOptions.length && !searchValue) {
      placeholderMessage = <p className="euiComboBoxPlaceholder">{placeholder}</p>;
    }

    const clickProps = {};

    if (!isDisabled && onClear && hasSelectedOptions) {
      clickProps.clear = {
        'data-test-subj': 'comboBoxClearButton',
        onClick: onClear
      };
    }

    let icon;

    if (!noIcon) {
      icon = {
        'aria-label': isListOpen ? 'Close list of options' : 'Open list of options',
        'data-test-subj': 'comboBoxToggleListButton',
        disabled: isDisabled,
        onClick: isListOpen && !isDisabled ? onCloseListClick : onOpenListClick,
        ref: toggleButtonRef,
        side: 'right',
        type: 'arrowDown'
      };
    }

    const wrapClasses = classNames('euiComboBox__inputWrap', {
      'euiComboBox__inputWrap--compressed': compressed,
      'euiComboBox__inputWrap--fullWidth': fullWidth,
      'euiComboBox__inputWrap--noWrap': singleSelection,
      'euiComboBox__inputWrap-isClearable': onClear,
      'euiComboBox__inputWrap--inGroup': prepend || append
    });
    return <EuiFormControlLayout icon={icon} {...clickProps} compressed={compressed} fullWidth={fullWidth} prepend={prepend} append={append}>
        <div className={wrapClasses} data-test-subj="comboBoxInput" onClick={onClick} tabIndex={-1} // becomes onBlur event's relatedTarget, otherwise relatedTarget is null when clicking on this div
      >
          {!singleSelection || !searchValue ? pills : null}
          {placeholderMessage}
          <AutosizeInput aria-activedescendant={focusedOptionId} aria-controls={isListOpen ? rootId('listbox') : ''} className="euiComboBox__input" data-test-subj="comboBoxSearchInput" disabled={isDisabled} id={id} inputRef={inputRef} onBlur={this.onBlur} onChange={this.inputOnChange} onFocus={this.onFocus} ref={this.inputRefCallback} role="textbox" style={{
          fontSize: 14
        }} value={searchValue} />
          {removeOptionMessage}
        </div>
      </EuiFormControlLayout>;
  }

}
EuiComboBoxInput.propTypes = {
  autoSizeInputRef: PropTypes.any,
  compressed: PropTypes.bool.isRequired,
  focusedOptionId: PropTypes.string,
  fullWidth: PropTypes.bool,
  hasSelectedOptions: PropTypes.bool.isRequired,
  id: PropTypes.string,
  inputRef: PropTypes.any,
  isDisabled: PropTypes.bool,
  isListOpen: PropTypes.bool.isRequired,
  noIcon: PropTypes.bool.isRequired,
  onBlur: PropTypes.any,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  onClick: PropTypes.func,
  onCloseListClick: PropTypes.func.isRequired,
  onFocus: PropTypes.any.isRequired,
  onOpenListClick: PropTypes.func.isRequired,
  onRemoveOption: PropTypes.func,
  placeholder: PropTypes.string,
  rootId: PropTypes.any.isRequired,
  searchValue: PropTypes.string.isRequired,
  selectedOptions: PropTypes.arrayOf(PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    isGroupLabelOption: PropTypes.bool,
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
      className: PropTypes.string,
      "aria-label": PropTypes.string,
      "data-test-subj": PropTypes.string,
      isGroupLabelOption: PropTypes.bool,
      label: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(PropTypes.any.isRequired),
      value: PropTypes.any
    }).isRequired),
    value: PropTypes.any
  }).isRequired),
  singleSelection: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.shape({
    asPlainText: PropTypes.bool
  }).isRequired]),
  toggleButtonRef: PropTypes.any,
  updatePosition: PropTypes.func.isRequired,
  value: PropTypes.string,
  prepend: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired).isRequired]),
  append: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired).isRequired]),
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};

try {
  // @ts-ignore
  EuiComboBoxInput.displayName = "EuiComboBoxInput"; // @ts-ignore

  EuiComboBoxInput.__docgenInfo = {
    "description": "",
    "displayName": "EuiComboBoxInput",
    "props": {
      "autoSizeInputRef": {
        "defaultValue": null,
        "description": "",
        "name": "autoSizeInputRef",
        "required": false,
        "type": {
          "name": "((instance: (AutosizeInput & HTMLInputElement) | null) => void) | undefined"
        }
      },
      "compressed": {
        "defaultValue": null,
        "description": "",
        "name": "compressed",
        "required": true,
        "type": {
          "name": "boolean"
        }
      },
      "focusedOptionId": {
        "defaultValue": null,
        "description": "",
        "name": "focusedOptionId",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "fullWidth": {
        "defaultValue": null,
        "description": "",
        "name": "fullWidth",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "hasSelectedOptions": {
        "defaultValue": null,
        "description": "",
        "name": "hasSelectedOptions",
        "required": true,
        "type": {
          "name": "boolean"
        }
      },
      "id": {
        "defaultValue": null,
        "description": "",
        "name": "id",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "inputRef": {
        "defaultValue": null,
        "description": "",
        "name": "inputRef",
        "required": false,
        "type": {
          "name": "((instance: HTMLInputElement | null) => void) | undefined"
        }
      },
      "isDisabled": {
        "defaultValue": null,
        "description": "",
        "name": "isDisabled",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "isListOpen": {
        "defaultValue": null,
        "description": "",
        "name": "isListOpen",
        "required": true,
        "type": {
          "name": "boolean"
        }
      },
      "noIcon": {
        "defaultValue": null,
        "description": "",
        "name": "noIcon",
        "required": true,
        "type": {
          "name": "boolean"
        }
      },
      "onBlur": {
        "defaultValue": null,
        "description": "",
        "name": "onBlur",
        "required": false,
        "type": {
          "name": "((event: FocusEvent<HTMLInputElement>) => void) | undefined"
        }
      },
      "onChange": {
        "defaultValue": null,
        "description": "",
        "name": "onChange",
        "required": false,
        "type": {
          "name": "((searchValue: string) => void) | undefined"
        }
      },
      "onClear": {
        "defaultValue": null,
        "description": "",
        "name": "onClear",
        "required": false,
        "type": {
          "name": "(() => void) | undefined"
        }
      },
      "onClick": {
        "defaultValue": null,
        "description": "",
        "name": "onClick",
        "required": false,
        "type": {
          "name": "(() => void) | undefined"
        }
      },
      "onCloseListClick": {
        "defaultValue": null,
        "description": "",
        "name": "onCloseListClick",
        "required": true,
        "type": {
          "name": "() => void"
        }
      },
      "onFocus": {
        "defaultValue": null,
        "description": "",
        "name": "onFocus",
        "required": true,
        "type": {
          "name": "(event: FocusEvent<HTMLInputElement>) => void"
        }
      },
      "onOpenListClick": {
        "defaultValue": null,
        "description": "",
        "name": "onOpenListClick",
        "required": true,
        "type": {
          "name": "() => void"
        }
      },
      "onRemoveOption": {
        "defaultValue": null,
        "description": "",
        "name": "onRemoveOption",
        "required": false,
        "type": {
          "name": "OptionHandler<T> | undefined"
        }
      },
      "placeholder": {
        "defaultValue": null,
        "description": "",
        "name": "placeholder",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "rootId": {
        "defaultValue": null,
        "description": "",
        "name": "rootId",
        "required": true,
        "type": {
          "name": "(idSuffix?: string) => string"
        }
      },
      "searchValue": {
        "defaultValue": null,
        "description": "",
        "name": "searchValue",
        "required": true,
        "type": {
          "name": "string"
        }
      },
      "selectedOptions": {
        "defaultValue": null,
        "description": "",
        "name": "selectedOptions",
        "required": false,
        "type": {
          "name": "EuiComboBoxOptionOption<T>[] | undefined"
        }
      },
      "singleSelection": {
        "defaultValue": null,
        "description": "",
        "name": "singleSelection",
        "required": false,
        "type": {
          "name": "boolean | EuiComboBoxSingleSelectionShape | undefined"
        }
      },
      "toggleButtonRef": {
        "defaultValue": null,
        "description": "",
        "name": "toggleButtonRef",
        "required": false,
        "type": {
          "name": "((instance: HTMLSpanElement | HTMLButtonElement | null) => void) | undefined"
        }
      },
      "updatePosition": {
        "defaultValue": null,
        "description": "",
        "name": "updatePosition",
        "required": true,
        "type": {
          "name": "UpdatePositionHandler"
        }
      },
      "value": {
        "defaultValue": null,
        "description": "",
        "name": "value",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "prepend": {
        "defaultValue": null,
        "description": "",
        "name": "prepend",
        "required": false,
        "type": {
          "name": "string | ReactElement<any, string | ((props: any) => ReactElement<any, string | ... | (new (props: any) => Component<any, any, any>)> | null) | (new (props: any) => Component<any, any, any>)> | StringOrReactElement[] | undefined"
        }
      },
      "append": {
        "defaultValue": null,
        "description": "",
        "name": "append",
        "required": false,
        "type": {
          "name": "string | ReactElement<any, string | ((props: any) => ReactElement<any, string | ... | (new (props: any) => Component<any, any, any>)> | null) | (new (props: any) => Component<any, any, any>)> | StringOrReactElement[] | undefined"
        }
      },
      "className": {
        "defaultValue": null,
        "description": "",
        "name": "className",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-label": {
        "defaultValue": null,
        "description": "",
        "name": "aria-label",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "data-test-subj": {
        "defaultValue": null,
        "description": "",
        "name": "data-test-subj",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/combo_box/combo_box_input/combo_box_input.tsx#EuiComboBoxInput"] = {
      docgenInfo: EuiComboBoxInput.__docgenInfo,
      name: "EuiComboBoxInput",
      path: "src/components/combo_box/combo_box_input/combo_box_input.tsx#EuiComboBoxInput"
    };
} catch (__react_docgen_typescript_loader_error) {}